import { Injectable } from "@angular/core";
import { environment } from "@env";
import { v4 } from "uuid";
import { BehaviorSubject } from "rxjs";
import { SignaloidWrapperService } from "./signaloid-wrapper.service";
import { makeErrorReadable, SnackbarService } from "design-system";
import { AVAILABLE_CHANNEL_PREFIXES, PUBLIC_CHANNEL_PREFIXES } from "../../models/ws-manager.models";
import { PosthogService } from "../monitoring/posthog.service";

@Injectable({
	providedIn: "root",
})
export class WsManagerService {
	private ws: WebSocket | undefined;
	private wsUrl = environment.REALTIME_ENDPOINT;
	private listeners: { [key in string]: BehaviorSubject<any> } = {};
	private connectionReady: boolean = false;
	private authorizationFree = false;
	private authorization = {
		host: environment.REALTIME_HOST,
		authorization: "test",
	};
	private subscriptions: { [key in AVAILABLE_CHANNEL_PREFIXES | PUBLIC_CHANNEL_PREFIXES | string]: string } = {
		[AVAILABLE_CHANNEL_PREFIXES.TASK_STATUS]: "",
		[AVAILABLE_CHANNEL_PREFIXES.BUILD_STATUS]: "",
		[AVAILABLE_CHANNEL_PREFIXES.BUILD_COUNT]: "",
		[AVAILABLE_CHANNEL_PREFIXES.TASK_COUNT]: "",
		[AVAILABLE_CHANNEL_PREFIXES.BUILD_STATS]: "",
		[AVAILABLE_CHANNEL_PREFIXES.TASK_STATS]: "",
		[PUBLIC_CHANNEL_PREFIXES.TASK_STATUS]: "",
	};

	public addAuthorizationBearer(bearer: string) {
		this.authorization.authorization = `Bearer ${bearer}`;
	}

	constructor(
		private signaloidService: SignaloidWrapperService,
		private snackbarService: SnackbarService,
		private posthog: PosthogService,
	) {
		this.authorizationFree = window.location.href.includes("demo-free");
	}

	public establishConnection() {
		this.wsUrl = this.authorizationFree ? environment.AUTH_FREE_REALTIME_ENDPOINT : environment.REALTIME_ENDPOINT;
		return new Promise<void>((resolve, reject) => {
			this.ws = new WebSocket(`${this.wsUrl}`, this.getAuthProtocol(this.authorization.authorization));
			this.ws.onopen = () => {
				this.ws?.send(
					JSON.stringify({
						type: "connection_init",
					}),
				);
			};

			this.ws.onmessage = (event) => {
				this.handleWsMessage(event, resolve);
			};
			this.ws.onclose = (event) => {
				this.posthog.capture("$websocket_close", {
					$websocket_close_message: event?.reason,
					$websocket_close_code: event?.code,
					$websocket_close_url: window.location.href,
					$websocket_close_userAgent: navigator.userAgent,
				});
				this.snackbarService.openSnackbar({
					header: "Connection closed",
					type: "error",
					description: "Please refresh the page and try again.",
				});
				this.connectionReady = false;
				reject(event);
			};
			this.ws.onerror = (error) => {
				this.connectionReady = false;
				this.snackbarService.openSnackbar({
					header: "Connection closed",
					type: "error",
					description: "Please refresh the page and try again.",
				});
				reject(error);
			};
		});
	}

	private handleWsMessage(event: MessageEvent<any>, resolve: (value: PromiseLike<void> | void) => void) {
		const message = JSON.parse(event.data);
		let messageBody = {};
		if (message?.event) {
			messageBody = JSON.parse(message.event);
		}
		if (message?.type === "connection_ack") {
			this.connectionReady = true;
			resolve();
		} else {
			if (this.listeners[message.id]) {
				this.listeners[message.id].next(messageBody);
			} else {
				console.warn("No active listener", message.id);
			}
		}

		if (message.type.includes("error")) {
			this.posthog.capture("$websocket_error", {
				$websocket_error_message: message?.error,
				$websocket_error_type: message?.type,
				$websocket_error_url: window.location.href,
				$websocket_error_userAgent: navigator.userAgent,
			});
			this.snackbarService.openSnackbar({
				header: "Connection Error",
				type: "error",
				description: makeErrorReadable(message.error),
			});
		}
	}

	public async subscribeToChannel(channel: AVAILABLE_CHANNEL_PREFIXES, subscriptionId: string = v4()) {
		if (this.subscriptions[channel]) {
			return this.subscriptions[channel];
		}
		if (this.ws || this.connectionReady) {
			const user = await this.signaloidService.getCurrentUser();
			const userId = user.UserID.split("_")[1];
			this.ws?.send(
				JSON.stringify({
					id: subscriptionId,
					type: "subscribe",
					channel: `${channel}/${userId}`,
					authorization: this.authorization,
				}),
			);
			this.subscriptions[channel] = subscriptionId;
			this.listeners[subscriptionId] = new BehaviorSubject<any>(null);
		} else {
			console.warn("No websocket connection");
		}
		return subscriptionId;
	}

	public async subscribeToPublicChannel(
		channel: PUBLIC_CHANNEL_PREFIXES,
		taskId: string,
		subscriptionId: string = v4(),
	) {
		const trimmedTaskId = taskId.split("_")[1];
		if (this.subscriptions[`${channel}/${trimmedTaskId}`]) {
			return this.subscriptions[`${channel}/${trimmedTaskId}`];
		}
		if (this.ws || this.connectionReady) {
			this.ws?.send(
				JSON.stringify({
					id: subscriptionId,
					type: "subscribe",
					channel: `${channel}/${trimmedTaskId}`,
					authorization: this.authorization,
				}),
			);
			this.subscriptions[`${channel}/${trimmedTaskId}`] = subscriptionId;
			this.listeners[subscriptionId] = new BehaviorSubject<any>(null);
		} else {
			console.warn("No websocket connection");
		}
		return subscriptionId;
	}

	public async unsubscribe(subscriptionId: string, channel: string) {
		if (this.listeners[subscriptionId]) {
			this.listeners[subscriptionId].complete();
			delete this.listeners[subscriptionId];
		} else {
			console.warn(`No listener found for subscriptionId ${subscriptionId}`);
		}
		const user = await this.signaloidService.getCurrentUser();
		const userId = user.UserID.split("_")[1];
		this.ws?.send(
			JSON.stringify({
				id: subscriptionId,
				type: "unsubscribe",
				channel: `${channel}/${userId}`,
				authorization: this.authorization,
			}),
		);
	}

	public unsubscribeToChannel(channel: AVAILABLE_CHANNEL_PREFIXES | string) {
		if (this.subscriptions[channel]) {
			this.ws?.send(
				JSON.stringify({
					id: this.subscriptions[channel],
					type: "unsubscribe",
					channel: `${channel}/${this.subscriptions[channel]}`,
					authorization: this.authorization,
				}),
			);
			this.subscriptions[channel] = "";
		}
	}

	public unsubscribeAllPublicChannels() {
		Object.keys(this.subscriptions).forEach((key) => {
			this.unsubscribeToChannel(key);
		});
	}

	public getListener(subscriptionId: string) {
		return this.listeners[subscriptionId];
	}

	private getTimestamp(): string {
		const date = new Date();
		return date.toISOString();
	}

	private getAuthProtocol(bearer?: string): string[] {
		if (this.authorizationFree) {
			this.authorization.host = environment.AUTH_FREE_REALTIME_HOST;
		}
		const header = btoa(
			JSON.stringify({
				host: this.authorizationFree ? environment.AUTH_FREE_REALTIME_HOST : environment.REALTIME_HOST,
				authorization: this.authorizationFree ? "test" : bearer,
			}),
		)
			.replace(/\+/g, "-")
			.replace(/\//g, "_")
			.replace(/=+$/, ""); // base64url
		return [`header-${header}`, "aws-appsync-event-ws"];
	}

	public stopConnection() {
		this.ws?.close();
		this.connectionReady = false;
	}

	public isConnected(): boolean {
		return this.connectionReady;
	}
}
